import type { BadgeTone } from '@hrms/ui';
import type { TrainingDisplayStatus } from '@hrms/shared';

/**
 * `@hrms/ui`'s `statusTone` deliberately leaves `completed` at its `info` fallback — two unrelated
 * one-on-one badges (`OneOnOneDetail.tsx`/`OneOnOneList.tsx`) already rely on that exact behaviour,
 * so a change there would silently repaint them (see that function's own comment). Training needs
 * `completed` -> success, so this is a small local override, the same pattern
 * `apps/web/src/routes/performance/reviews/status.ts`'s `reviewStatusTone` uses for its own
 * module-specific vocabulary — not a second copy of the whole switch, just the one case
 * `@hrms/ui` can't own.
 */
export function trainingDisplayTone(status: TrainingDisplayStatus): BadgeTone {
  if (status === 'completed') return 'success';
  return statusToneFallback(status);
}

/** Mirrors `@hrms/ui`'s `statusTone` switch for every training value it already gets right —
 *  duplicated only because that function can't be called with an argument that skips its
 *  (deliberately wrong, for training) `completed` case. Keep this in sync with Badge.tsx's own
 *  training block if the plan's tone table (Training_Module_Plan.md §6.2) ever changes. */
function statusToneFallback(status: TrainingDisplayStatus): BadgeTone {
  switch (status) {
    case 'not_started':
    case 'cancelled':
      return 'neutral';
    case 'started':
    case 'in_progress':
      return 'info';
    case 'completed_late':
    case 'incomplete':
    case 'due_soon':
      return 'warning';
    case 'overdue':
      return 'danger';
    default:
      return 'info';
  }
}

export const TRAINING_DISPLAY_LABEL: Record<TrainingDisplayStatus, string> = {
  not_started: 'To Start',
  started: 'Started',
  in_progress: 'In Progress',
  completed: 'Completed',
  completed_late: 'Completed (late)',
  incomplete: 'Incomplete',
  overdue: 'Overdue',
  due_soon: 'Due soon',
  cancelled: 'Cancelled',
};

/** What a training assignment with no deadline (`due: { kind: 'none' }`, `dueAt: null`) shows
 *  wherever a due date would otherwise render — the course stays available to view any time. */
export const NO_DUE_DATE_LABEL = 'No due date';

/**
 * A no-deadline assignment can never be overdue or due soon. The API derives `displayStatus`
 * (plan D2) and should never send either for a null `dueAt`, but the UI must not paint danger or
 * warning styling off a deadline that does not exist, so this folds those two back to the plain
 * in-progress state rather than trusting it blindly.
 */
export function dueAwareDisplayStatus(
  status: TrainingDisplayStatus,
  dueAt: string | null,
): TrainingDisplayStatus {
  if (dueAt === null && (status === 'overdue' || status === 'due_soon')) return 'in_progress';
  return status;
}

/** Sort comparator: earliest `dueAt` first, no-deadline assignments last (stable among nulls). */
export function compareDueAtNullsLast(
  a: { dueAt: string | null },
  b: { dueAt: string | null },
): number {
  if (a.dueAt === null && b.dueAt === null) return 0;
  if (a.dueAt === null) return 1;
  if (b.dueAt === null) return -1;
  return new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime();
}

/**
 * Pulls a structured error `code` (e.g. `CONSENT_REQUIRED`, `CAMERA_REQUIRED`,
 * `REQUIREMENTS_NOT_MET`, `MODULE_LOCKED`) out of a ts-rest error, checking both the top-level
 * `AppError` shape (`body.code`) and the attendance-gate style nested shape (`body.details.code`)
 * that `apps/api/src/lib/training-gate.ts`-adjacent 412s use — the contract's summaries don't pin
 * down which one each route takes, so this checks both rather than guessing wrong for half of them.
 * The nested code wins: a `PreconditionFailedError` always carries the generic top-level
 * `PRECONDITION_FAILED`, and the specific reason only lives in `details.code`.
 */
export function trainingErrorCode(error: unknown): string | undefined {
  if (!error || typeof error !== 'object' || !('body' in error)) return undefined;
  const body = (error as { body?: unknown }).body;
  if (!body || typeof body !== 'object') return undefined;
  const { code, details } = body as { code?: unknown; details?: unknown };
  if (details && typeof details === 'object' && 'code' in details) {
    const nested = (details as { code?: unknown }).code;
    if (typeof nested === 'string') return nested;
  }
  if (typeof code === 'string') return code;
  return undefined;
}
